import { DiscrepancyKind, WasteManifestSource } from "generated-graphql/graphql";

import { FieldWarningIndicator } from "./Manifests/FieldWarningIndicator";
import { SOURCE_LABEL, SOURCE_ORDER } from "./Manifests/manifestSourceDisplay";
import {
  collectFieldDisagreementLines,
  collectFieldIssueLabels,
  countDiscrepanciesOnField,
  FieldDisagreementLine,
  filterOpenDiscrepancies,
  linesShowCrossSourceDisagreement,
  sourcesDisagree,
  WasteStreamDiscrepancyRow,
} from "./wasteStreamDiscrepancyIndicator";
import { WasteStreamDiscrepancyTooltip } from "./WasteStreamDiscrepancyTooltip";

interface WasteStreamFieldWarningProps {
  discrepancies: ReadonlyArray<WasteStreamDiscrepancyRow>;
  /** Raw `ManifestDiscrepancy.field` value, e.g. "formCode" or "quantity". */
  field: string;
  /** Human-readable field name shown as the tooltip header. */
  fieldLabel: string;
  /**
   * Also surface the field-issue (CE rule) findings on the stream and exclude
   * them from the comparison count — used for State Waste Codes, where rule
   * findings and value comparisons share one cell.
   */
  includeFieldIssues?: boolean;
  /**
   * The row's raw per-source values for this field, when the surface has them
   * (the per-line drawer grid). Lets the indicator flag a source disagreement
   * even when no formal discrepancy row exists yet — the silent-disagreement
   * signal the drawer grid carried before. Omitted by the aggregated list grids.
   */
  sourceValues?: Partial<Record<WasteManifestSource, string | null | undefined>>;
  /** Nudge the icon off the preceding text (matches the grid's inline cells). */
  inline?: boolean;
  /**
   * Scopes which discrepancy statuses count as "open" for this indicator: staff
   * see PENDING + UNRESOLVED, non-staff see PENDING only. RESOLVED and IGNORED
   * are never open, for either viewer.
   */
  isStaff: boolean;
}

/** Ordered per-source lines from raw values, only when they actually disagree. */
function rawDisagreementLines(
  sourceValues: Partial<Record<WasteManifestSource, string | null | undefined>> | undefined
): FieldDisagreementLine[] {
  if (!sourceValues) return [];
  const present = SOURCE_ORDER.map((source) => ({ source, value: sourceValues[source] })).filter(
    (s): s is { source: WasteManifestSource; value: string } => s.value != null && s.value !== ""
  );
  if (!sourcesDisagree(present.map((s) => s.value))) return [];
  return present.map(({ source, value }) => ({ source, label: SOURCE_LABEL[source], value }));
}

/**
 * The grid's per-cell discrepancy (!) indicator. Hovering shows the actual
 * disagreeing per-source values (and any field-issue findings) for the field,
 * so users no longer have to open the manifest to see what conflicts. Formal
 * discrepancy rows win; a raw source disagreement with no row still surfaces
 * (via `sourceValues`). Renders nothing when there's nothing to flag.
 */
export function WasteStreamFieldWarning({
  discrepancies,
  field,
  fieldLabel,
  includeFieldIssues = false,
  sourceValues,
  inline,
  isStaff,
}: WasteStreamFieldWarningProps) {
  // Only open discrepancies drive the indicator. RESOLVED/IGNORED rows remain in
  // the feed (for historical counts elsewhere) but their frozen raise-time
  // snapshots must not surface as a live conflict next to a corrected cell.
  // Non-staff further narrow to PENDING only — UNRESOLVED is staff review state.
  const openDiscrepancies = filterOpenDiscrepancies(discrepancies, isStaff);

  const discrepancyLines = collectFieldDisagreementLines(openDiscrepancies, field);
  const issueLabels = includeFieldIssues ? collectFieldIssueLabels(openDiscrepancies, field) : [];
  // State Waste Codes counts only comparison rows here; its field-issue rows
  // surface via issueLabels instead, so they aren't double-counted.
  const count = countDiscrepanciesOnField(
    includeFieldIssues
      ? openDiscrepancies.filter((d) => d.kind !== DiscrepancyKind.FieldIssue)
      : openDiscrepancies,
    field
  );

  // Prefer the discrepancy rows' values, but only when they show a genuine
  // cross-source conflict (≥2 distinct sources). Several values from a single
  // source — e.g. two one-sided quantity discrepancies on different lines — are
  // not a disagreement and would otherwise render as a fake "e-Manifest: 100 /
  // e-Manifest: 200" cross-source conflict, suppressing the accurate count text.
  // Otherwise fall back to the raw source values (already disagreement-gated),
  // else nothing (count fallback handles it).
  const lines = linesShowCrossSourceDisagreement(discrepancyLines)
    ? discrepancyLines
    : rawDisagreementLines(sourceValues);

  if (count <= 0 && lines.length === 0 && issueLabels.length === 0) {
    return null;
  }

  // The count summary is only shown when there are no value rows. Don't say
  // "sources disagree" for a cell whose only signal is a CE rule finding (count
  // 0, no per-source values) — that misattributes a rule finding as a conflict.
  const countText = count > 0 ? `${count} ${count === 1 ? "discrepancy" : "discrepancies"}` : "";
  let ariaSummary: string;
  if (countText) {
    ariaSummary = countText;
  } else if (lines.length > 0) {
    ariaSummary = "sources disagree";
  } else {
    ariaSummary = `${issueLabels.length} ${issueLabels.length === 1 ? "issue" : "issues"}`;
  }

  return (
    <FieldWarningIndicator
      tooltip={
        <WasteStreamDiscrepancyTooltip
          fieldLabel={fieldLabel}
          lines={lines}
          issueLabels={issueLabels}
          fallback={countText || undefined}
        />
      }
      ariaLabel={`${ariaSummary} on ${fieldLabel}`}
      inline={inline}
    />
  );
}
